"use client";

import { motion, AnimatePresence } from "framer-motion";
import { buttonVariants } from "@/components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Modal, ModalContent, ModalBody } from "@/components/ui/modal";
import useIsMobile from "@/hooks/use-is-mobile";
import { cn } from "@/lib/utils";
import { type ActiveFilter, type FilterFieldDef } from "./types";
import { FilterPanel } from "./filter-panel";
import { Icon } from "@/components/ui/icon";

export function FilterButton({
  fields,
  filters,
  onFiltersChange,
  open,
  onOpenChange,
}: {
  fields: FilterFieldDef[];
  filters: ActiveFilter[];
  onFiltersChange: (filters: ActiveFilter[]) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();

  const triggerClassName = cn(
    buttonVariants({ variant: "secondary", size: "sm" }),
    filters.length > 0
      ? "border-solid bg-contrast/6"
      : "border-dashed bg-contrast/4",
  );

  const triggerContent = (
    <>
      <Icon name="IconFilter2" className="size-3.5" />
      Filter
      <AnimatePresence>
        {filters.length > 0 && (
          <motion.span
            initial={{ scale: 0.8, opacity: 0, width: 0 }}
            animate={{ scale: 1, opacity: 1, width: "auto" }}
            exit={{ scale: 0.8, opacity: 0, width: 0 }}
            transition={{ duration: 0.12, ease: [0.25, 0.1, 0.25, 1] }}
            className="flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-contrast/10 px-1 text-[11px] font-medium text-contrast/70 origin-center overflow-hidden"
          >
            {filters.length}
          </motion.span>
        )}
      </AnimatePresence>
    </>
  );

  // Mobile: open the filters in a bottom drawer (Modal renders a vaul Drawer
  // below 1024px) instead of a popover cramped against the small trigger.
  if (isMobile) {
    return (
      <>
        <motion.button
          layout="size"
          transition={{ duration: 0.12, ease: [0.25, 0.1, 0.25, 1] }}
          type="button"
          onClick={() => onOpenChange(true)}
          className={triggerClassName}
        >
          {triggerContent}
        </motion.button>
        <Modal open={open} onOpenChange={onOpenChange}>
          <ModalContent>
            <ModalBody className="p-0">
              <FilterPanel
                fields={fields}
                filters={filters}
                onFiltersChange={onFiltersChange}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <motion.button
          layout="size"
          transition={{ duration: 0.12, ease: [0.25, 0.1, 0.25, 1] }}
          type="button"
          className={triggerClassName}
        >
          {triggerContent}
        </motion.button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6}>
        <FilterPanel
          fields={fields}
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
      </PopoverContent>
    </Popover>
  );
}
